/**
 * Client-side image transcoding.
 *
 * The upload is a direct browser → Spaces PUT whose signature binds the exact
 * byte length, content type and checksum, so the API never sees the original
 * bytes and could not convert them even if it wanted to. Re-encoding therefore
 * happens here, before the file is hashed and its upload authorized.
 */

/** Re-encoded to WebP. Anything else — PDFs — passes through untouched. */
const CONVERTIBLE = new Set(['image/jpeg', 'image/png', 'image/webp']);

/** Long-edge cap: larger than any viewport, with room left to crop later. */
const MAX_EDGE = 4096;

/** Tried in order until one lands inside the budget. */
const QUALITY_LADDER = [0.9, 0.82, 0.72, 0.6];

/** How far dimensions shrink when even the lowest quality will not fit. */
const SHRINK_STEP = 0.75;

export const isConvertible = (mimeType: string) => CONVERTIBLE.has(mimeType);

const withWebpExtension = (filename: string) => `${filename.replace(/\.[^./\\]+$/, '')}.webp`;

/** Something drawn that can be saved as WebP at a given quality. */
export interface Surface {
  encode(quality: number): Promise<Blob>;
}

/** A `<canvas>` already drawn on — the fallback below, and a rendered PDF page. */
export function canvasSurface(canvas: HTMLCanvasElement): Surface {
  return {
    encode: (quality) =>
      new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (blob) =>
            blob ? resolve(blob) : reject(new Error('The browser returned no image data.')),
          'image/webp',
          quality,
        );
      }),
  };
}

/**
 * The first encoding along `ladder` that fits in `budgetBytes`, for the small
 * images made from a file — previews and display sizes. Rejects when none fits,
 * and when the browser cannot save WebP at all.
 */
export async function encodeWithin(
  surface: Surface,
  budgetBytes: number,
  ladder: readonly number[],
): Promise<Blob> {
  for (const quality of ladder) {
    const blob = await surface.encode(quality);
    // Safari before 16 hands back a PNG instead of failing.
    if (blob.type !== 'image/webp') throw new Error('This browser cannot save WebP.');
    if (blob.size <= budgetBytes) return blob;
  }
  throw new Error('The image would not fit within its size limit.');
}

/**
 * OffscreenCanvas where it exists, a detached `<canvas>` otherwise. Both encode
 * WebP; only the call to get the blob back differs.
 */
export function drawTo(bitmap: ImageBitmap, width: number, height: number): Surface {
  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(width, height);
    const context = canvas.getContext('2d');
    if (!context) throw new Error('This browser could not open a canvas to convert the image.');
    context.drawImage(bitmap, 0, 0, width, height);
    return { encode: (quality) => canvas.convertToBlob({ type: 'image/webp', quality }) };
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('This browser could not open a canvas to convert the image.');
  context.drawImage(bitmap, 0, 0, width, height);
  return canvasSurface(canvas);
}

/**
 * Returns a WebP copy of `file` no larger than `budgetBytes`, or the file
 * itself when it is not an image.
 */
export async function toWebp(file: File, budgetBytes: number): Promise<File> {
  if (!isConvertible(file.type)) return file;

  // Re-encoding a WebP that already fits only loses detail to no purpose.
  if (file.type === 'image/webp' && file.size <= budgetBytes) return file;

  // `from-image` applies the EXIF rotation. Without it, photos taken in
  // portrait arrive on their side, because the tag is dropped on re-encode.
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  let scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));

  try {
    for (let attempt = 0; attempt < 4; attempt += 1) {
      const surface = drawTo(
        bitmap,
        Math.max(1, Math.round(bitmap.width * scale)),
        Math.max(1, Math.round(bitmap.height * scale)),
      );

      for (const quality of QUALITY_LADDER) {
        const blob = await surface.encode(quality);

        // Safari before 16 ignores the type and hands back a PNG instead of
        // failing, which would upload as the wrong content type.
        if (blob.type !== 'image/webp') {
          throw new Error(
            'This browser cannot save WebP. Use a current Chrome, Firefox or Safari.',
          );
        }

        if (blob.size <= budgetBytes) {
          return new File([blob], withWebpExtension(file.name), { type: 'image/webp' });
        }
      }

      scale *= SHRINK_STEP;
    }

    throw new Error('This image could not be compressed below the size limit.');
  } finally {
    bitmap.close();
  }
}
